/**
 * Activity → successor links + arrowheads (MapActivityLinks.vue / MapArrow.vue):
 * a bezier from each activity to its successors, coloured by the source's mode;
 * a violated link (source deadline after target — e.g. a relation kept through
 * a force move) is dashed AND error red in every link/view mode.
 * Each ends in a filled arrowhead. Links are culled when neither endpoint is
 * near the viewport.
 *
 * Which links are visible follows the connection toggle (`mapStore.linkMode`,
 * cycled none → single → multi → all): `all` shows every link, `none` shows none,
 * and `single`/`multi` show only the links of the selected and hovered activities
 * (multi additionally fans one hop out to neighbours) — mirroring
 * MapActivityLinks.linksFromSelectedActivity / linksFromHoveredActivity.
 */
import { isRelationViolatedByDeadline } from '@/utils/helpers/violatedEdges';
import { linkColor, modeOf } from '../../draw/palette';
import { layoutAnchor } from '../../layout/activityLayout';
import { activeLinkKeys } from './linkGeometry';
import type { SceneContext } from '../../engine/SceneContext';
import type { ItemMode } from '@/common';
import type { MapActivity } from '@/store/ioStore';

/**
 * Stroke mode + dash for the link a → b. A violated relation (the source ends
 * after the target, day granularity) is error red and dashed regardless of the
 * link/view mode — the mirrored wrong-order case is just an instance of the
 * same rule. Template maps carry no deadlines ('' vs ''), so nothing is ever
 * marked there.
 */
export function linkStyle(
  a: Pick<MapActivity, 'deadline'>,
  b: Pick<MapActivity, 'deadline'>,
  mode: ItemMode,
): { mode: ItemMode; lineDash: number[]; violated: boolean } {
  const violated = isRelationViolatedByDeadline(a.deadline, b.deadline);

  return {
    mode: violated ? 'underway-red' : mode,
    lineDash: violated ? [3, 5] : [],
    violated,
  };
}

export function drawLinks(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
): void {
  const { map, io } = scene;
  // Use the FULL activity list (not the content frame's visible/unhidden subset)
  // so links touching a hidden workstream/phase are still drawn: their endpoint
  // resolves into the placeholder row via getActivityY (hidden workstream → Y
  // 230) / activityPos (date-based X), exactly as MapActivityLinks/MapArrow do.
  // Hidden activities are never removed from the relationship graph in SVG.
  const all = io.activities;
  const byId = new Map(all.map((a) => [a.id, a]));

  const active = activeLinkKeys(map, byId);
  if (active && active.size === 0) {
    return; // linkMode 'none', or nothing selected/hovered in single/multi.
  }
  // The selected link (if any) renders thicker — MapArrow `stroke-width 2 vs 1.5`.
  const selLink = map.selectedContext === 'link' ? map.selectedLink : ['', ''];

  // Wrap so the per-link strokeStyle/lineWidth/lineDash stay self-contained and
  // don't leak into drawHexagons (the next content painter).
  ctx.save();
  const minX = -100;
  const maxX = scene.width + 200;
  // Cull per-endpoint: a hidden endpoint is never in the content frame's
  // `visible` set, yet its placeholder-row position can be on-screen, so draw
  // whenever EITHER endpoint's projected X is near the viewport. Project each
  // source ONCE here (not once per successor — `activityPos`/`screenX` aren't
  // free on a dense graph) and reuse it for every outgoing link.
  for (const a of all) {
    if (a.successors.length === 0) {
      continue;
    }
    // Endpoints anchor to what the user sees: a collapsed group member's link
    // attaches to its MARKER, not to the invisible natural position (PO
    // feedback 2026-06-11) — `layoutAnchor` resolves both cases.
    const aGroup = scene.layout?.markerOf(a.id) ?? null;
    const { x: ax, y: ay } = layoutAnchor(scene, a);
    const aNear = ax >= minX && ax <= maxX;
    const aMode = modeOf(a.state, a.status);

    for (const successorId of a.successors) {
      const b = byId.get(successorId);
      if (!b) {
        continue;
      }
      // Connection toggle: only draw links in the active set (null = draw all).
      if (active && !active.has(`${a.id}-${successorId}`)) {
        continue;
      }
      // Both ends collapsed into the SAME marker → the link has nothing to
      // connect (it would be a squiggle from the marker to itself).
      if (aGroup && aGroup === scene.layout?.markerOf(successorId)) {
        continue;
      }
      const { x: bxRaw, y: by } = layoutAnchor(scene, b);
      // Cull links where neither endpoint is near the viewport.
      if (!aNear && (bxRaw < minX || bxRaw > maxX)) {
        continue;
      }
      const bx = bxRaw - 22;

      // A violated (out-of-order) relation is dashed AND error red.
      const style = linkStyle(a, b, aMode);
      ctx.strokeStyle = linkColor(style.mode);
      ctx.setLineDash(style.lineDash);
      ctx.lineWidth =
        selLink[0] === a.id && selLink[1] === successorId ? 2 : 1.5;

      ctx.beginPath();
      ctx.moveTo(ax, ay);
      ctx.bezierCurveTo(ax + 30, ay, bx - 80, by, bx, by);
      ctx.stroke();

      ctx.setLineDash([]);
      drawArrowhead(ctx, bx, by, ctx.strokeStyle as string);
    }
  }
  ctx.restore();
}

function drawArrowhead(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  color: string,
): void {
  const size = 6;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x - size, y - size / 2);
  ctx.lineTo(x - size, y + size / 2);
  ctx.closePath();
  ctx.fill();
}
